<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { FieldConfig, FormConfig, FormValues, Locale } from '@/forms/types'
import TextField from './fields/TextField.vue'
import TextareaField from './fields/TextareaField.vue'
import SelectField from './fields/SelectField.vue'
import RadioField from './fields/RadioField.vue'
import CheckboxField from './fields/CheckboxField.vue'
import CheckboxGroupField from './fields/CheckboxGroupField.vue'

defineProps<{
  config: FormConfig
  locale: Locale
  values: FormValues
  errors: Record<string, string>
  isVisible: (field: FieldConfig) => boolean
}>()

const { t } = useI18n()
</script>

<template>
  <form
    class="form"
    :aria-label="config.title?.[locale] ?? config.id"
    novalidate
    @submit.prevent="$emit('submit')"
  >
    <header v-if="config.title || config.intro" class="form-header">
      <h1 v-if="config.title" class="title">{{ config.title[locale] }}</h1>
      <p v-if="config.intro" class="intro">{{ config.intro[locale] }}</p>
    </header>

    <div class="fields">
      <template v-for="field in config.fields" :key="field.id">
        <template v-if="isVisible(field)">
          <div v-if="field.type === 'section'" class="section">
            <h2 class="section-title">{{ field.label[locale] }}</h2>
            <p v-if="field.description" class="section-intro">{{ field.description[locale] }}</p>
          </div>
          <TextField
            v-else-if="field.type === 'text' || field.type === 'email'"
            :field="field"
            :locale="locale"
            :model-value="values[field.id] as string"
            :error="errors[field.id]"
            @update:model-value="values[field.id] = $event"
          />
          <TextareaField
            v-else-if="field.type === 'textarea'"
            :field="field"
            :locale="locale"
            :model-value="values[field.id] as string"
            :error="errors[field.id]"
            @update:model-value="values[field.id] = $event"
          />
          <SelectField
            v-else-if="field.type === 'select'"
            :field="field"
            :locale="locale"
            :model-value="values[field.id] as string"
            :error="errors[field.id]"
            @update:model-value="values[field.id] = $event"
          />
          <RadioField
            v-else-if="field.type === 'radio'"
            :field="field"
            :locale="locale"
            :model-value="values[field.id] as string"
            :error="errors[field.id]"
            @update:model-value="values[field.id] = $event"
          />
          <CheckboxField
            v-else-if="field.type === 'checkbox'"
            :field="field"
            :locale="locale"
            :model-value="values[field.id] as boolean"
            :error="errors[field.id]"
            @update:model-value="values[field.id] = $event"
          />
          <CheckboxGroupField
            v-else-if="field.type === 'checkbox-group'"
            :field="field"
            :locale="locale"
            :model-value="values[field.id] as string[]"
            :error="errors[field.id]"
            @update:model-value="values[field.id] = $event"
          />
        </template>
      </template>
    </div>

    <hr class="divider" />

    <CheckboxField
      class="consent"
      :field="config.consentField"
      :locale="locale"
      :model-value="values[config.consentField.id] as boolean"
      :error="errors[config.consentField.id]"
      @update:model-value="values[config.consentField.id] = $event"
    />

    <slot name="below-fields" />

    <p class="required-hint"><span class="req" aria-hidden="true">*</span> {{ t('requiredHint') }}</p>

    <button type="submit" class="submit" :disabled="$attrs['data-submitting'] === 'true'">
      <slot name="submit-label">{{ config.submitLabel[locale] }}</slot>
    </button>
  </form>
</template>

<style scoped>
.form {
  display: block;
}
.form-header {
  margin-bottom: var(--space-7);
}
.title {
  font-size: var(--font-size-2xl);
  margin: 0 0 var(--space-3);
}
.intro {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-base);
  line-height: var(--line-height-loose);
}
.fields {
  display: block;
}
.section {
  margin: var(--space-7) 0 var(--space-5);
}
.section:first-child {
  margin-top: 0;
}
.section-title {
  font-size: var(--font-size-lg);
  margin: 0 0 var(--space-2);
  padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--color-border);
}
.section-intro {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-loose);
}
.divider {
  margin: var(--space-6) 0 var(--space-5);
  border: 0;
  border-top: 1px solid var(--color-border);
}
.consent {
  margin-bottom: var(--space-5);
}
.required-hint {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}
.required-hint .req {
  color: var(--color-brand-dark);
}
.submit {
  display: inline-block;
  width: 100%;
  max-width: 18rem;
  padding: var(--space-4) var(--space-7);
  margin-top: var(--space-5);
  background: var(--color-text);
  color: white;
  border: 1px solid var(--color-text);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-wider);
  text-transform: uppercase;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
}
.submit:hover:not(:disabled) {
  background: var(--color-bg);
  color: var(--color-text);
}
.submit:disabled {
  opacity: 0.5;
  cursor: progress;
}

@media (max-width: 480px) {
  .submit {
    max-width: 100%;
  }
}
</style>
